import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { Survey, SurveyFilters } from "../../../modules/common-modules/Survey/types";

interface SurveyPagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

interface SurveyState {
  // Survey list state
  surveys: Survey[];
  loading: boolean;
  error: string | null;

  // Filter state
  filters: SurveyFilters;

  // Pagination state
  pagination: SurveyPagination;

  // Detail view state
  selectedSurvey: Survey | null;
  detailLoading: boolean;
  detailError: string | null;

  // Operation states
  deleting: boolean;
  publishing: boolean;
  closing: boolean;
}

const initialFilters: SurveyFilters = {
  search: '',
  status: '',
  surveyType: '',
  audienceType: '',
  fromDate: '',
  toDate: '',
  createdBy: '',
};

const initialPagination: SurveyPagination = {
  page: 1,
  limit: 10,
  total: 0,
  totalPages: 0,
};

const initialState: SurveyState = {
  surveys: [],
  loading: false,
  error: null,
  filters: initialFilters,
  pagination: initialPagination,
  selectedSurvey: null,
  detailLoading: false,
  detailError: null,
  deleting: false,
  publishing: false,
  closing: false,
};

const surveySlice = createSlice({
  name: "survey",
  initialState,
  reducers: {
    // Survey list actions
    fetchSurveysStart(state: SurveyState) {
      state.loading = true;
      state.error = null;
    },
    fetchSurveysSuccess(state: SurveyState, action: PayloadAction<{
      surveys: Survey[];
      total: number;
      totalPages: number;
      page: number;
      limit: number;
    }>) {
      state.surveys = action.payload.surveys;
      state.pagination = {
        page: action.payload.page,
        limit: action.payload.limit,
        total: action.payload.total,
        totalPages: action.payload.totalPages,
      };
      state.loading = false;
      state.error = null;
    },
    fetchSurveysError(state: SurveyState, action: PayloadAction<string>) {
      state.loading = false;
      state.error = action.payload;
      state.surveys = [];
    },

    // Filter actions
    setFilters(state: SurveyState, action: PayloadAction<SurveyFilters>) {
      state.filters = action.payload;
      // Reset to first page when filters change
      state.pagination.page = 1;
    },
    clearFilters(state: SurveyState) {
      state.filters = initialFilters;
      state.pagination.page = 1;
    },

    // Pagination actions
    setPagination(state: SurveyState, action: PayloadAction<Partial<SurveyPagination>>) {
      state.pagination = { ...state.pagination, ...action.payload };
    },

    // Survey detail actions
    fetchSurveyDetailStart(state: SurveyState) {
      state.detailLoading = true;
      state.detailError = null;
    },
    fetchSurveyDetailSuccess(state: SurveyState, action: PayloadAction<Survey>) {
      state.selectedSurvey = action.payload;
      state.detailLoading = false;
      state.detailError = null;
    },
    fetchSurveyDetailError(state: SurveyState, action: PayloadAction<string>) {
      state.detailLoading = false;
      state.detailError = action.payload;
      state.selectedSurvey = null;
    },

    // Delete actions
    deleteSurveyStart(state: SurveyState) {
      state.deleting = true;
    },
    deleteSurveySuccess(state: SurveyState, action: PayloadAction<string>) {
      state.surveys = state.surveys.filter(survey => survey.id !== action.payload);
      state.deleting = false;
      // Update pagination total
      state.pagination.total = Math.max(0, state.pagination.total - 1);
      state.pagination.totalPages = Math.ceil(state.pagination.total / state.pagination.limit);
    },
    deleteSurveyError(state: SurveyState) {
      state.deleting = false;
    },

    // Publish actions
    publishSurveyStart(state: SurveyState) {
      state.publishing = true;
    },
    publishSurveySuccess(state: SurveyState, action: PayloadAction<string>) {
      // Update survey status in list
      const surveyIndex = state.surveys.findIndex(survey => survey.id === action.payload);
      if (surveyIndex !== -1) {
        state.surveys[surveyIndex].status = 'ACTIVE';
      }
      // Update selected survey if it's the same
      if (state.selectedSurvey?.id === action.payload) {
        state.selectedSurvey.status = 'ACTIVE';
      }
      state.publishing = false;
    },
    publishSurveyError(state: SurveyState) {
      state.publishing = false;
    },

    // Close actions
    closeSurveyStart(state: SurveyState) {
      state.closing = true;
    },
    closeSurveySuccess(state: SurveyState, action: PayloadAction<string>) {
      // Update survey status in list
      const surveyIndex = state.surveys.findIndex(survey => survey.id === action.payload);
      if (surveyIndex !== -1) {
        state.surveys[surveyIndex].status = 'INACTIVE';
      }
      // Update selected survey if it's the same
      if (state.selectedSurvey?.id === action.payload) {
        state.selectedSurvey.status = 'INACTIVE';
      }
      state.closing = false;
    },
    closeSurveyError(state: SurveyState) {
      state.closing = false;
    },

    // Clear actions
    clearSelectedSurvey(state: SurveyState) {
      state.selectedSurvey = null;
      state.detailError = null;
    },
    clearErrors(state: SurveyState) {
      state.error = null;
      state.detailError = null;
    },
  },
});

export const {
  fetchSurveysStart,
  fetchSurveysSuccess,
  fetchSurveysError,
  setFilters,
  clearFilters,
  setPagination,
  fetchSurveyDetailStart,
  fetchSurveyDetailSuccess,
  fetchSurveyDetailError,
  deleteSurveyStart,
  deleteSurveySuccess,
  deleteSurveyError,
  publishSurveyStart,
  publishSurveySuccess,
  publishSurveyError,
  closeSurveyStart,
  closeSurveySuccess,
  closeSurveyError,
  clearSelectedSurvey,
  clearErrors,
} = surveySlice.actions;

export default surveySlice.reducer;